import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type AccentId = 'terracotta' | 'sage' | 'ochre' | 'blueberry' | 'plum' | 'juniper';

// Also read by the pre-paint inline script in index.html — keep in sync.
export const UI_STORAGE_KEY = 'recipe_keeper_ui';

interface UIState {
  // Sidebar
  sidebarOpen: boolean;

  // Modals
  activeModal: string | null;
  modalData: Record<string, unknown> | null;

  // Theme
  theme: ThemePreference;
  accent: AccentId;
}

interface UIActions {
  // Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Modals
  openModal: (modalId: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;

  // Theme
  setTheme: (theme: ThemePreference) => void;
  setAccent: (accent: AccentId) => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>()(
  persist(
    set => ({
      // Initial state
      sidebarOpen: true,
      activeModal: null,
      modalData: null,
      theme: 'system',
      accent: 'terracotta',

      // Actions
      toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: open => set({ sidebarOpen: open }),

      openModal: (modalId, data = {}) => set({ activeModal: modalId, modalData: data }),
      closeModal: () => set({ activeModal: null, modalData: null }),

      setTheme: theme => set({ theme }),
      setAccent: accent => set({ accent }),
    }),
    {
      name: UI_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({ theme: state.theme, accent: state.accent }),
    },
  ),
);
